import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import _debounce from 'lodash/debounce';
import type {
  WCardTemplatePhotoPreviewView,
  WPhotoPreviewDimensionsView,
} from '@zola/svc-web-api-ts-client';
import { autocropUploadcareFile } from 'components/common/UploadcareWidget/utils/crop';
import { getPLPPhotoPreviewState } from 'cards/selectors/plpPhotoPreview';
import { getImageUrl } from 'paper/utils/helpers';
import { SHAPES } from 'paper/components/CardOptionsPreview/constants';
import { isCardTypeWithAdditionalShapes } from 'cards/constants/CardTypes';
import { isPhotoPreviewSupported } from '../constants';
import { useSessionStorageData } from '../hook';
import {
  Layers,
  PhotoLayer,
  NonPreviewPhoto,
  TransparentLayer,
  getLayersStyles,
  getPhotoLayerStyles,
  getTransparentLayerStyles,
  ClipRatio,
  Box,
} from './styles';

const CLIP_RATIO: ClipRatio = { wRatio: 1, hRatio: 1 };

// Returns the dimensions of the box wrapping all layers
const getWrapperBox = (
  wrapperElement: HTMLElement,
  cardDimensions: WPhotoPreviewDimensionsView,
  isSquareFilter: boolean,
  isLandscape: boolean
): Box => {
  const { width: cardWidth = 1, height: cardHeight = 1 } = cardDimensions;
  const boundingBox = wrapperElement.getBoundingClientRect();
  let boxWidth: number;
  let boxHeight: number;
  if (isLandscape) {
    boxWidth = (boundingBox.width || 315) - 5;
    boxHeight = isSquareFilter ? boxWidth : (cardHeight / cardWidth) * boxWidth;
  } else {
    boxHeight = (boundingBox.height || 315) - 5;
    boxWidth = isSquareFilter ? boxHeight : (cardWidth / cardHeight) * boxHeight;
  }

  return { x: 0, y: 0, width: boxWidth, height: boxHeight };
};

const getPhotoBox = (
  cardDimensions: WPhotoPreviewDimensionsView,
  wrapperBox: Box,
  slot: WPhotoPreviewDimensionsView
): Box => {
  const { width: cardWidth = 1, height: cardHeight = 1 } = cardDimensions;
  const { x: slotX = 0, y: slotY = 0, width: slotWidth = 1, height: slotHeight = 1 } = slot;

  return {
    x: wrapperBox.width * (slotX / cardWidth),
    y: wrapperBox.height * (slotY / cardHeight),
    width: wrapperBox.width * (slotWidth / cardWidth),
    height: wrapperBox.height * (slotHeight / cardHeight),
  };
};

export interface PreviewProps {
  cardType: string;
  filteredShape: SHAPES;
  isDigital?: boolean;
  template?: WCardTemplatePhotoPreviewView;
  altText?: string;
  defaultImage?: string;
  Fallback: () => JSX.Element | null;
  orientation?: string;
  bypassLazy?: boolean;
}

const Preview = ({
  cardType,
  isDigital,
  template,
  altText,
  filteredShape,
  defaultImage,
  Fallback,
  orientation,
  bypassLazy,
}: PreviewProps): JSX.Element | null => {
  const enableShapesPreview = useMemo(() => isCardTypeWithAdditionalShapes(cardType), [cardType]);

  const plpPhotoPreviewState = useSelector(getPLPPhotoPreviewState);

  const { currentUploadcareFile } = useSessionStorageData(cardType, isDigital);
  const resolvedUploadcareFiles = useMemo(
    () =>
      (plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile) &&
      template?.slots &&
      template.slots.map(slot =>
        autocropUploadcareFile(
          plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile,
          slot,
          true
        )
      ),
    [plpPhotoPreviewState?.uploadcareFile, currentUploadcareFile, template]
  );

  /* Default card plp image (square filter) is a square image with added whitespace, 
  while other shapes use an aspect ratio that matches the card itself */
  // Will always be false with a custom photo
  const isSquareFilter =
    enableShapesPreview &&
    filteredShape === SHAPES.square &&
    !plpPhotoPreviewState?.uploadcareFile &&
    !currentUploadcareFile;
  const isLandscape = orientation === 'LANDSCAPE';

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [layerStyles, setLayerStyles] = useState<React.CSSProperties | undefined>();
  const [transparentLayerStyles, setTransparentLayerStyles] = useState<
    React.CSSProperties | undefined
  >();
  const [photoLayerStyles, setPhotoLayerStyles] = useState<Array<React.CSSProperties | undefined>>(
    []
  );

  useEffect(() => {
    const updateStyles = () => {
      if (!wrapperRef.current || !template?.card_dimensions) return;

      const cardDimensions = template.card_dimensions;
      const wrapperBox = getWrapperBox(
        wrapperRef.current,
        cardDimensions,
        isSquareFilter,
        isLandscape
      );

      setLayerStyles(getLayersStyles(wrapperBox, CLIP_RATIO));
      setTransparentLayerStyles(getTransparentLayerStyles(wrapperBox, CLIP_RATIO));

      // Set photo layer styles (if slot data is available)
      if (!template?.slots) return;
      const newPhotoLayerStyles = template.slots.map(slot => {
        const photoBox = getPhotoBox(cardDimensions, wrapperBox, slot);
        return getPhotoLayerStyles(photoBox, wrapperBox, CLIP_RATIO);
      });
      setPhotoLayerStyles(newPhotoLayerStyles);
    };
    updateStyles();

    const debouncedUpdate = _debounce(() => updateStyles(), 800);
    window.addEventListener('resize', debouncedUpdate);
    return () => window.removeEventListener('resize', debouncedUpdate);
  }, [template, isSquareFilter, isLandscape]);

  const previewAvailable =
    isPhotoPreviewSupported(cardType) &&
    !!template?.slots &&
    !!(plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile);

  return (
    <Box ref={wrapperRef}>
      {!previewAvailable && !enableShapesPreview && <Fallback />}
      {!previewAvailable && enableShapesPreview && (
        <Layers noShadow>
          <NonPreviewPhoto src={defaultImage} alt={altText} bypassLazy={bypassLazy} />
        </Layers>
      )}
      {previewAvailable && (
        <Layers
          style={layerStyles}
          noShadow={enableShapesPreview && filteredShape !== SHAPES.square}
        >
          {photoLayerStyles?.map((style, index) => (
            <PhotoLayer
              style={style}
              data-testid="live-preview-photo-layer"
              key={index}
              src={resolvedUploadcareFiles?.[index]?.cdnUrl}
              alt="Preview with photo"
            />
          ))}
          <TransparentLayer
            style={transparentLayerStyles}
            src={getImageUrl(
              enableShapesPreview ? template?.image_uuids?.[filteredShape] : template.image_uuid
            )}
            alt={altText}
            bypassLazy={bypassLazy}
          />
        </Layers>
      )}
    </Box>
  );
};

export default Preview;
